// Los modos que ofrece el sheet de "Generar" de mobile, para cuando NO lo monta la
// home — ADR-2026-08-04-la-barra-inferior-vive-en-el-shell.
//
// `InterfazV2` arma su lista desde `design-row`, una colección que construye en su
// propio render y que fuera de esa pantalla no existe. El shell monta la barra en
// TODAS sus pantallas, así que necesita la misma lista sin depender de la home.
//
// Se rearma desde las mismas fuentes que ya comparten `/modos` y el drawer de "¿Qué
// querés generar?" de Mi marca: `MODES` con sus feature flags, `anuncioTercero` para
// el orden, `caratulaModo` para las portadas y `getModePlanLock` para el candado.
// Cada modo abre por URL (`/interfaz-v2/modo/<path>`) con `presetData` en el state,
// que es el mecanismo con el que `InterfazV2` monta el sheet en el primer paint y no
// se ve un flash de la home. Los que declaran `route` en el catálogo tienen pantalla
// propia fuera de `InterfazV2` y abren ahí, sin state (ver `onAbrir`).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MODES,
  anuncioTercero,
  carruselUltimo,
  modoDestacadoGenerar,
  modosVisibles,
  nombreCortoModo,
  caratulaModo,
  caratulaCuadradaModo,
} from './modesCatalog';
import { HERRAMIENTAS_EXTRA } from '../estilos/contenidoAtlas';
import { EDICIONES_RAPIDAS, candadoEdicionRapida } from '../estilos/EstiloMinimal';
import {
  getAgenteLock,
  getModePlanLock,
  getPlanRequirementNote,
} from '../../lib/planCapabilities';
// Del módulo HOJA y no del barril `chat-atlas`: ese barril arrastra el motor del chat
// entero y este hook lo monta el shell de mobile en todas las pantallas
// (`rutaChatAtlas.ts` es sólo constantes y funciones puras).
import { RUTA_CHAT_ATLAS } from '../../chat-atlas/ui/rutaChatAtlas';
import { getModeExamples, type ModeExample } from './modeExamples';
import { EstudioVideoCover } from '../../components/EstudioVideoCover';
import { getUserPlan } from '../../lib/planUtils';
import { useUserGroup } from '../../hooks/useUserGroup';
import {
  esRubroBelleza,
  esRubroHogarDeco,
  esRubroModa,
  rubroVeCatalogoConAgente,
  rubroVePlantillasBelleza,
} from '../../lib/rubro';
import type { SujetoDeGeneracion } from '../../components/SheetModosProducto';
import type { AgenteSheet, EdicionSheet, ModoSheet } from './components/SheetGenerar';

/**
 * Plantillas como una opción más del sheet —
 * ADR-2026-08-04-plantillas-es-una-opcion-de-generar.
 *
 * No es un modo de `MODES` sino un atajo al catálogo, así que se arma acá con la misma
 * entrada que ya usan la sidebar y el drawer de Mi marca (`HERRAMIENTAS_EXTRA`).
 *
 * Aparece en las DOS familias porque una plantilla se puede generar como imagen o como
 * video —es el toggle que ya vive en el preview del preset—, y desde qué familia se
 * entró es lo que decide con cuál arranca: `?media=video` viaja hasta `InterfazV2` y
 * deja el toggle en Video. Sin eso, entrar por Videos y encontrarse generando una
 * imagen sería contradecir la elección del paso anterior.
 *
 * Sólo para los rubros que ven plantillas — el llamador lo decide con
 * `conPlantillas`, la misma bandera que usa el segundo lugar de la barra inferior.
 *
 * Son DOS modos, uno por rubro y excluyentes entre sí
 * (ADR-2026-09-10-plantillas-belleza-modo-propio): Moda abre su catálogo y Belleza y
 * Cuidado Personal el suyo. La card es la misma —mismo nombre, misma carátula— y lo
 * único que cambia es a cuál de las dos pantallas lleva, que es lo que resuelve
 * `plantillasDelRubro`.
 */
const RUTA_PLANTILLAS = '/interfaz-v2/plantillas';
const RUTA_PLANTILLAS_BELLEZA = '/interfaz-v2/plantillas-belleza';

/**
 * La entrada de `HERRAMIENTAS_EXTRA` y la ruta del modo de plantillas que ve este
 * rubro, o `null` si no ve ninguno.
 */
const plantillasDelRubro = (rubro: string | null) => {
  const id = rubroVePlantillasBelleza(rubro) ? 'plantillas-belleza' : 'plantillas';
  const herramienta = HERRAMIENTAS_EXTRA.find((h) => h.id === id);
  if (!herramienta) return null;
  return {
    herramienta,
    ruta: id === 'plantillas-belleza' ? RUTA_PLANTILLAS_BELLEZA : RUTA_PLANTILLAS,
  };
};

/**
 * Payload del visor de ejemplos de un modo BLOQUEADO por plan —
 * ADR-2026-08-25-sheet-generar-fixes-mobile.
 *
 * Es lo que el llamador le pasa a `ModeExamplesModal` (con `actionLabel="Mejorar
 * plan"`): tocar una card con candado abre esto en vez de expulsar directo a
 * `/subscription`, que es el mismo comportamiento que la home V2 y `/modos`. El
 * modal lo renderiza el LLAMADOR y no el sheet: el tap cierra el sheet (y en Mi
 * marca lo desmonta), así que el visor tiene que vivir en alguien que siga en
 * pantalla.
 */
export interface EjemplosModoBloqueado {
  title: string;
  description?: string;
  examples: ModeExample[];
  planNote: string;
}

export function useModosGenerar(opciones?: {
  /**
   * Sobre qué se está generando, cuando el sheet se abre desde una grilla (un producto
   * o un modelo de Mi marca). Filtra los modos —desde un modelo, sólo los que lo
   * aceptan— y viaja al modo elegido para que abra con eso puesto.
   */
  sujeto?: SujetoDeGeneracion | null;
  /**
   * La marca activa ve ALGÚN modo de Plantillas — el de Moda o el de Belleza y
   * Cuidado Personal. Cuál de los dos lo decide el `rubro` de acá abajo.
   */
  conPlantillas?: boolean;
  /**
   * Rubro de la marca activa (con el de la empresa como respaldo). Decide si entra
   * Infografía (ADR-2026-08-05-infografia-la-ve-el-rubro-hogar-y-decoracion) y qué carátula
   * muestra Catálogo (ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro).
   *
   * Va como rubro crudo y no como un `conInfografia` al lado de `conPlantillas`: el
   * rubro decide DOS cosas distintas —qué modos hay y con qué foto se ven—, y un
   * booleano por consecuencia obligaría al llamador a saber todas.
   */
  rubro?: string | null;
}): {
  modos: ModoSheet[];
  /** Herramientas que no son modos (Flujos), para la sección Herramientas del sheet de destinos. */
  herramientasExtra: ModoSheet[];
  /** La card del Agente del paso 1, o `null` si no corresponde. Ver el memo. */
  agente: AgenteSheet | null;
  ediciones: EdicionSheet[];
  /** Visor de ejemplos de un modo bloqueado (`null` = cerrado). Ver el tipo. */
  ejemplos: EjemplosModoBloqueado | null;
  cerrarEjemplos: () => void;
  /** El CTA del visor ("Mejorar plan"): la pantalla de planes, como el resto del sheet. */
  irAPlanes: () => void;
} {
  const navigate = useNavigate();
  const { isDevTester } = useUserGroup();
  const sujeto = opciones?.sujeto ?? null;
  const conPlantillas = opciones?.conPlantillas ?? false;
  const rubro = opciones?.rubro ?? null;
  // El sujeto se va en `null` al cerrar y el sheet sigue montado un rato por la
  // animación. `esModelo` sale del sujeto vivo y es de lo que depende la lista, en vez
  // del objeto entero: así no se rearma al pasar de un producto a otro.
  const esModelo = sujeto?.tipo === 'modelo';
  // Ídem — ADR-2026-08-20-desde-un-producto-tambien-hay-modos-que-no-aplican. Ojo: acá
  // el sujeto casi siempre es `null` (el sheet de la barra inferior se abre sin nada
  // elegido), y ahí `esProducto` queda en `false` y la lista NO se recorta, que es lo
  // correcto: sin producto, Animar y Carrusel sí tienen sentido.
  const esProducto = sujeto?.tipo === 'producto';

  // Plan para el gating, con el mismo arranque que `/modos` y el drawer de Mi marca:
  // el cache que llena el Sidebar —para tener candados desde el primer paint— y el
  // valor real después. Con el plan desconocido no se bloquea nada, que es lo que
  // devuelve `getModePlanLock` con `null`.
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelado = false;
    // El plan real, sin lentes: el panel CONTEXTO DEV y sus overrides se eliminaron
    // (supersede ADR-2026-08-03-el-plan-simulado-tambien-gatea).
    getUserPlan().then((p) => {
      if (!cancelado && p) setPlan(p);
    });
    return () => {
      cancelado = true;
    };
  }, []);

  // Visor de ejemplos del modo bloqueado que se tocó. Vive acá y no en el sheet
  // porque el tap cierra (y puede desmontar) el sheet — ver `EjemplosModoBloqueado`.
  const [ejemplos, setEjemplos] = useState<EjemplosModoBloqueado | null>(null);

  const esHogarDeco = esRubroHogarDeco(rubro);
  // El gemelo para Belleza y Cuidado Personal (`soloBelleza`) —
  // ADR-2026-09-10-modo-mostrar-como-funciona-belleza.
  const esBelleza = esRubroBelleza(rubro);
  // El gemelo para Moda e Indumentaria (`soloModa`) —
  // ADR-2026-09-17-el-modo-diseno-es-solo-para-moda.
  const esModa = esRubroModa(rubro);
  const veCatalogoConAgente = rubroVeCatalogoConAgente(rubro);
  const modos = useMemo<ModoSheet[]>(() => {
    // Cuál va en grande arriba del sheet. Se decide ACÁ y viaja marcado en cada modo:
    // `SheetGenerar` lo tenía como un `path` fijo (`'modelo'`) y no conoce el rubro —
    // ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro.
    const pathDestacado = modoDestacadoGenerar(esHogarDeco);
    // Los mismos filtros que `/modos`, la home, el drawer de Mi marca y la sidebar,
    // ahora en un solo lugar —
    // ADR-2026-08-20-el-filtro-de-modos-estaba-copiado-en-cuatro-lugares.
    const lista = modosVisibles(MODES, {
      isDevTester,
      esHogarDeco,
      esBelleza,
      esModa,
      veCatalogoConAgente,
      esModelo,
      esProducto,
    }).map((m): ModoSheet => {
      // La carátula (póster + video) sale de UN solo lugar: la escalera entera
      // —portada del rubro → pisada de interfaz → catálogo → preset— vive en
      // `caratulaModo` (ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo).
      const caratula = caratulaModo(m.path, rubro, m.preset.imageUrl);
      // El lock ENTERO y no sólo el booleano: si el modo tiene ejemplos cargados, el
      // visor necesita `lock.feature` para la nota del plan.
      const lock = getModePlanLock(m.preset.kind, plan);
      const bloqueado = !!lock;
      // Una sola vez por modo: lo usan el pill "Ver ejemplos" de la card (via
      // `conEjemplos`) y el tap de acá abajo, y tienen que decir lo mismo.
      const ejemplosDelModo = getModeExamples(m.preset.kind);
      return {
        path: m.path,
        titulo: nombreCortoModo(m.path, m.preset.title),
               imagen: caratula.poster,
        // La 1:1, para las cards cuadradas del sheet
        // (ADR-2026-08-04-la-portada-cuadrada-no-es-de-mobile). Va aparte y no pisando
        // a `imagen` porque el sheet dibuja las dos formas: la card destacada es 3:2 —
        // la proporción de la carátula del catálogo— y la grilla es 1:1.
        // El orden de preferencia lo resuelve `caratulaCuadradaModo`: la 1:1 del rubro,
        // después la tabla `PORTADA_CUADRADA` y, de última, la apaisada recortada.
        imagenCuadrada: caratulaCuadradaModo(m.path, rubro, m.preset.imageUrl),
        // La carátula en VIVO, para que la card 3:2 del sheet la reproduzca en loop
        // con `imagen` de póster (ADR-2026-08-25-sheet-generar-fixes-mobile). Con
        // carátula propia del rubro no hay video —esa portada es la que se quiere
        // ver—, y eso ya lo decide `caratulaModo`.
        video: caratula.video,
        familia: m.preset.outputType === 'video' ? 'video' : 'imagen',
        destacado: m.path === pathDestacado,
        bloqueado,
        // El pill "Ver ejemplos" de la card bloqueada — sólo si el visor de verdad
        // va a abrir (ADR-2026-08-25-pill-ver-ejemplos-en-cards-bloqueadas).
        conEjemplos: bloqueado && ejemplosDelModo.length > 0,
        onAbrir: () => {
          // Bloqueado → la pantalla de planes, igual que una card con candado
          // (ADR-2026-08-03-modo-bloqueado-abre-pricing). Acá siempre se NAVEGA: el
          // catálogo como overlay lo provee el shell de desktop, que en mobile no está.
          if (bloqueado) {
            // Con ejemplos cargados, el visor (ejemplos + nota del plan + "Mejorar
            // plan") — lo mismo que hacen la home V2 y /modos al tocar una card con
            // candado. Sin ejemplos, directo a la pantalla de planes como antes: acá
            // no existe el FeatureLockedModal del shell.
            if (lock && ejemplosDelModo.length > 0) {
              setEjemplos({
                title: m.preset.title,
                description: m.preset.description,
                examples: ejemplosDelModo,
                planNote: getPlanRequirementNote(lock.feature),
              });
              return;
            }
            navigate('/subscription');
            return;
          }
          /* Los modos con PANTALLA PROPIA se abren por su ruta —
             ADR-2026-08-20-los-modos-con-pantalla-propia-se-abren-por-su-ruta.

             Esto era un CLICK MUERTO en producción: acá se armaba siempre
             `/interfaz-v2/modo/<path>`, pero `InterfazV2` sólo resuelve los modos que
             monta ella (su `MODE_PATH_TO_KIND` interno tiene nueve entradas escritas a
             mano y no conoce carrusel, animar-imagen ni shorts-studio). Tocar
             "Carrusel" o "Animar" en este sheet no hacía absolutamente nada.

             Lo mismo pasaba con los tres modos de Hogar y Decoración (Variantes de
             acabado, Mostrar cómo funciona, Ambiente completo): declaran `route` y
             viven fuera de `InterfazV2`, así que desde el sheet de Generar en mobile
             caían en la home muda —
             ADR-2026-09-10-el-sheet-de-generar-respeta-la-ruta-propia-del-modo.

             `m.route` es justamente el campo que el catálogo tiene para esto, y
             `/modos` ya lo respetaba. Los modos con ruta propia no reciben el `state`:
             son pantallas que arrancan eligiendo imagen y no leen `presetData`. */
          if (m.route) {
            navigate(m.route);
            return;
          }
          navigate(`/interfaz-v2/modo/${m.path}`, {
            state: {
              presetData: m.preset,
              collectionPresets: [],
              // Lo que se tocó en la grilla de Mi marca, si el sheet se abrió desde
              // ahí: el producto saltea el paso de elegirlo
              // (ADR-2026-08-04-el-producto-entra-con-el-modo) y el modelo abre el modo
              // con ese modelo ya puesto en el picker.
              productosPreseleccionados:
                sujeto?.tipo === 'producto' ? [sujeto.producto] : [],
              modeloPreseleccionado:
                sujeto?.tipo === 'modelo'
                  ? {
                      // `mode: 'preset'` = modelo guardado, la misma forma que arma el
                      // ModelPicker al elegirlo de la biblioteca.
                      mode: 'preset' as const,
                      id: String(sujeto.modelo.id),
                      name: sujeto.modelo.nombre,
                      imageUrl: sujeto.modelo.thumbnailUrl,
                    }
                  : undefined,
            },
          });
        },
      };
    });
    // Anuncio tercero, la misma posición que ocupa en la tira y en la sidebar
    // (ADR-2026-08-03-anuncio-tercero-tira-y-sidebar).
    // Y para Hogar y Decoración, Carrusel al final: en el orden natural del catálogo
    // caía PRIMERO en la familia de imágenes, siendo el modo menos importante de ese
    // rubro — ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro.
    const conAnuncio = anuncioTercero(lista, (m) => m.path);
    const ordenados = esHogarDeco
      ? carruselUltimo(conAnuncio, (m) => m.path)
      : conAnuncio;

    /* Acá estaban las dos cards del Agente, una por familia. Se fueron al paso 1 del
       sheet, como card propia debajo de Imágenes y Videos —
       ADR-2026-09-20-el-agente-es-una-card-del-paso-1, supersede
       ADR-2026-09-14-el-agente-es-de-pro-y-ultra en cuanto a DÓNDE vive la puerta (el
       piso de plan no cambia). Ver `agente`, más abajo. */
    /* El Estudio de Video en la familia Videos —
       ADR-2026-09-25-el-estudio-de-video-entra-al-sheet-de-generar, el gemelo de mobile de
       ADR-2026-09-24-el-estudio-de-video-entra-al-popup-de-la-home. Como el Agente, no es
       un modo de `MODES` y va sólo sin sujeto: abre la lista de proyectos, que no tiene
       dónde recibir el producto o el modelo tocado. Candado por el mismo
       `getModePlanLock` que la sidebar y la home (Ultra); sin ejemplos cargados, el tap
       bloqueado va a planes. */
    const estudio = HERRAMIENTAS_EXTRA.find((h) => h.id === 'editor-video');
    const lockEstudio = getModePlanLock('editor-video', plan);
    const cardEstudio: ModoSheet[] = estudio
      ? [
          {
            path: 'editor-video',
            titulo: nombreCortoModo('editor-video', estudio.nombre),
            imagen: estudio.poster,
            Caratula: EstudioVideoCover,
            familia: 'video',
            bloqueado: !!lockEstudio,
            onAbrir: () => navigate(lockEstudio ? '/subscription' : estudio.ruta),
          },
        ]
      : [];
    // Sin sujeto, como el Agente antes de mudarse al paso 1: con un producto o un modelo
    // tocado, la lista de proyectos no tiene dónde recibirlo.
    const conEstudio = sujeto ? ordenados : [...ordenados, ...cardEstudio];

    if (!conPlantillas) return conEstudio;
    const modoPlantillas = plantillasDelRubro(rubro);
    if (!modoPlantillas) return conEstudio;
    const { herramienta: plantillas, ruta: rutaPlantillas } = modoPlantillas;
    /** La misma card en las dos familias; cambia con qué media abre el catálogo. */
    const cardPlantillas = (familia: 'imagen' | 'video'): ModoSheet => ({
      // `path` acá es sólo la key de la lista, así que las dos entradas necesitan una
      // distinta aunque lleven al mismo lugar.
      path: familia === 'video' ? 'plantillas-video' : 'plantillas',
      titulo: nombreCortoModo(plantillas.id, plantillas.nombre),
      imagen: caratulaModo(plantillas.id, rubro, plantillas.poster).poster,
      familia,
      // El catálogo se mira con cualquier plan: el candado, si toca, lo pone la
      // plantilla al generar.
      bloqueado: false,
      onAbrir: () =>
        navigate(familia === 'video' ? `${rutaPlantillas}?media=video` : rutaPlantillas),
    });
    return [...conEstudio, cardPlantillas('imagen'), cardPlantillas('video')];
  }, [plan, isDevTester, navigate, esModelo, esProducto, esModa, sujeto, conPlantillas, rubro]);

  /**
   * La card del Agente del paso 1 — ADR-2026-09-20-el-agente-es-una-card-del-paso-1.
   *
   * En mobile el Agente no tenía NINGUNA puerta (la barra inferior son cuatro tabs más
   * Generar, y el flotante es de escritorio); esta card es esa puerta —
   * ADR-2026-09-14-el-agente-es-de-pro-y-ultra. Estaba como una card de modo DENTRO de
   * cada familia, como Plantillas, y se mudó al paso 1: el Agente no es un tipo de
   * salida sino otra forma de trabajar, y aparecer dos veces —una por familia— obligaba
   * a elegir imagen o video para una herramienta que hace las dos cosas.
   *
   * No es un modo de `MODES`: no tiene `preset`, no abre `InterfazV2` y su piso no sale
   * de `getModePlanLock`. Tampoco tiene carátula: la card la dibuja el sheet con la
   * mascota sobre un degradé (ADR-2026-09-20-la-card-del-agente-es-la-mascota), así que
   * acá sólo viajan el candado y a dónde lleva el toque.
   *
   * `null` cuando hay sujeto: desde una grilla de Mi marca el sheet se abre SOBRE un
   * producto o un modelo, y todas las demás cards lo llevan puesto (`presetData` con el
   * producto preseleccionado). El Agente no tiene dónde recibirlo, así que ahí la card
   * sería la única que tira a la basura lo que acabás de tocar.
   *
   * El piso de plan sigue siendo el mismo (`getAgenteLock`, Pro y Ultra —
   * ADR-2026-09-14-el-agente-es-de-pro-y-ultra): bloqueado se dibuja con candado y el
   * tap va a planes, que es lo que ya hacía como card de modo.
   */
  const agente = useMemo<AgenteSheet | null>(() => {
    if (sujeto) return null;
    const lock = getAgenteLock(plan);
    return {
      bloqueado: !!lock,
      onAbrir: () => navigate(lock ? '/subscription' : RUTA_CHAT_ATLAS),
    };
  }, [plan, sujeto, navigate]);

  // Los atajos de "Ediciones rápidas": el mismo catálogo que la home y que la sección
  // de desktop, con la navegación atada acá.
  const ediciones = useMemo<EdicionSheet[]>(
    () =>
      EDICIONES_RAPIDAS.map((e) => {
        // El MISMO candado que la sección de la home, vía el helper compartido —
        // ADR-2026-08-20-los-atajos-de-carrusel-y-animar-van-a-su-modo. Sin un punto
        // común, el atajo de Animar frenaba en desktop y entraba en mobile.
        const lock = candadoEdicionRapida(e, plan);
        return {
          label: e.label,
          desc: e.desc,
          Icono: e.Icono,
          bloqueado: !!lock,
          // Bloqueado va a la pantalla de planes y no al modo. Acá se NAVEGA en vez de
          // abrir el overlay del catálogo: este sheet vive en mobile, bajo
          // `DashboardLayout`, donde el shell —y con él `abrirPricing`— no existe. Es
          // el mismo criterio que ya usa `SheetModosProducto` para sus modos
          // bloqueados.
          onAbrir: () => navigate(lock ? '/subscription' : e.ruta),
        };
      }),
    [navigate, plan],
  );

  /**
   * Herramientas que no son modos de `MODES` ni van en el sheet de Generar: hoy, Flujos. Las
   * lista la sección Herramientas del sheet de destinos de la interfaz nueva
   * (ADR-2026-10-08-la-interfaz-nueva-llega-al-telefono), igual que el panel de la sidebar.
   * Mismo candado por plan que la sidebar (`getModePlanLock`).
   */
  const herramientasExtra = useMemo<ModoSheet[]>(() => {
    const flujos = HERRAMIENTAS_EXTRA.find((h) => h.id === 'flujos');
    if (!flujos) return [];
    const lock = getModePlanLock('flujos', plan);
    return [
      {
        path: 'flujos',
        titulo: nombreCortoModo('flujos', flujos.nombre),
        imagen: flujos.poster,
        familia: 'imagen',
        bloqueado: !!lock,
        onAbrir: () => navigate(lock ? '/subscription' : flujos.ruta),
      },
    ];
  }, [plan, navigate]);

  return {
    modos,
    herramientasExtra,
    agente,
    ediciones,
    ejemplos,
    cerrarEjemplos: () => setEjemplos(null),
    irAPlanes: () => navigate('/subscription'),
  };
}
